import {Tooltip} from 'platform/components';
import {
  Box,
  Center,
  Display,
  HStack,
  Icon,
  Integer,
  Spinner,
  Text,
  useResponsivePropValue,
  ValueByDevice,
  VStack,
} from 'platform/foundation';
import styled from 'styled-components';

import {useId} from 'react';

import {isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';
import {SkeletonLoadingBox} from '@dms/shared';

import {Nullish, TestIdProps, suffixTestId} from 'shared/web';

import {HoverableBox} from '../../HoverableBox';
import {WorkshopOverviewDensity} from '../types/WorkshopOverviewDensity';
import {WorkshopOverviewInteraction} from '../types/WorkshopOverviewInteraction';
import {WorkshopOverviewTileConfig} from '../types/WorkshopOverviewTileConfig';
import {WorkshopSectionAppearance} from '../types/WorkshopSectionAppearance';

interface WorkshopOverviewTileProps extends TestIdProps {
  tile: WorkshopOverviewTileConfig;
  count: number | Nullish;
  isPending: boolean;
  isDisabled: boolean;
  disabledReason?: string;
  density: WorkshopOverviewDensity;
  appearance: WorkshopSectionAppearance;
  interaction: WorkshopOverviewInteraction;
}

interface WorkshopOverviewTileContentProps {
  tile: WorkshopOverviewTileConfig;
  count: number | Nullish;
  isPending: boolean;
  appearance: WorkshopSectionAppearance;
}

interface WorkshopOverviewTilePadding {
  vertical: Integer;
  horizontal: Integer;
}

const SKELETON_HEIGHT: ValueByDevice<Integer> = [5, 5, 10];
const COMPACT_SKELETON_WIDTH = 6;
const COMPACT_SKELETON_HEIGHT = 4;
const COMPACT_ICON_SLOT = 5;

const TILE_PADDING: Record<WorkshopOverviewDensity, WorkshopOverviewTilePadding> = {
  comfortable: {vertical: 3, horizontal: 3},
  compact: {vertical: 1, horizontal: 2},
};

export function WorkshopOverviewTile(props: WorkshopOverviewTileProps) {
  const padding = TILE_PADDING[props.density];
  const disabledReasonId = useId();
  const hasDisabledReason = props.isDisabled && isNotNil(props.disabledReason);

  const onOpenTile = () => {
    if (!props.isDisabled) {
      props.interaction.onOpenTile(props.tile);
    }
  };

  const onPrefetchTile = () => {
    if (!props.isDisabled) {
      props.interaction.onPrefetchTile(props.tile);
    }
  };

  return (
    <Tooltip
      key={hasDisabledReason ? 'disabled' : 'available'}
      label={hasDisabledReason ? <span role="tooltip">{props.disabledReason}</span> : undefined}
      placement="top"
      childrenWrapperDisplay="flex"
      shouldWrapperStretch
    >
      <HoverableBox
        width="100%"
        height="100%"
        borderRadius="medium"
        backgroundColor={props.appearance.tileBackgroundColor}
        borderColor={props.appearance.tileBorderColor}
        hoverBackgroundColor={props.appearance.tileHoverBackgroundColor}
        hoverBorderColor={props.appearance.tileHoverBorderColor}
      >
        <StyledTileButton
          type="button"
          tabIndex={props.isDisabled && !hasDisabledReason ? -1 : 0}
          aria-disabled={props.isDisabled || undefined}
          aria-describedby={hasDisabledReason ? disabledReasonId : undefined}
          onClick={onOpenTile}
          onMouseEnter={onPrefetchTile}
          onFocus={onPrefetchTile}
          data-testid={suffixTestId('workshopOverviewTile', props)}
        >
          <Box
            height="100%"
            paddingVertical={padding.vertical}
            paddingHorizontal={padding.horizontal}
          >
            {props.density === 'compact' ? (
              <CompactTileContent
                tile={props.tile}
                count={props.count}
                isPending={props.isPending}
                appearance={props.appearance}
              />
            ) : (
              <ComfortableTileContent
                tile={props.tile}
                count={props.count}
                isPending={props.isPending}
                appearance={props.appearance}
              />
            )}
          </Box>
        </StyledTileButton>
      </HoverableBox>
      <span id={disabledReasonId} hidden>
        {props.disabledReason}
      </span>
    </Tooltip>
  );
}

// Clickable removes disabled tab stops and does not expose aria-describedby.
const StyledTileButton = styled.button`
  all: unset;
  display: block;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  cursor: pointer;
  user-select: none;

  &[aria-disabled='true'] {
    cursor: initial;
  }

  &:focus-visible {
    outline: var(--outline-default) var(--color-general-accent);
    outline-offset: var(--outline-offset);
  }
`;

function ComfortableTileContent(props: WorkshopOverviewTileContentProps) {
  const skeletonHeight = useResponsivePropValue(SKELETON_HEIGHT);

  return (
    <HStack spacing={3} align="center" height="100%">
      <Box
        width={12}
        height={12}
        borderRadius="small"
        backgroundColor={props.appearance.iconBackgroundColor}
      >
        <Center height="100%">
          {props.isPending ? (
            <Spinner size="small" />
          ) : (
            <Icon value={props.tile.icon} size={5} color={props.appearance.accentColor} />
          )}
        </Center>
      </Box>
      <VStack spacing={1} grow={1}>
        <Text size="xSmall" color="secondary">
          {i18n.t(props.tile.labelKey)}
        </Text>
        <SkeletonLoadingBox
          key={isNil(props.count) ? 'loading' : 'count'}
          width={14}
          height={skeletonHeight}
          borderRadius="small"
          backgroundColor={props.appearance.skeletonColor}
          isLoading={isNil(props.count)}
        >
          <Display size={[2, 2, 1]}>{props.count?.toString() ?? ''}</Display>
        </SkeletonLoadingBox>
      </VStack>
    </HStack>
  );
}

function CompactTileContent(props: WorkshopOverviewTileContentProps) {
  const label = i18n.t(props.tile.labelKey);

  return (
    <HStack spacing={2} align="center" height="100%">
      <Center width={COMPACT_ICON_SLOT} height={COMPACT_ICON_SLOT} shrink={0}>
        {props.isPending ? (
          <Spinner size="small" />
        ) : (
          <Icon value={props.tile.icon} size={4} color={props.appearance.accentColor} />
        )}
      </Center>
      <Box flex={1} minWidth={0}>
        <Text size="xSmall" color="secondary" noWrap title={label}>
          {label}
        </Text>
      </Box>
      <SkeletonLoadingBox
        key={isNil(props.count) ? 'loading' : 'count'}
        width={COMPACT_SKELETON_WIDTH}
        height={COMPACT_SKELETON_HEIGHT}
        contentStyles={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}
        borderRadius="small"
        backgroundColor={props.appearance.skeletonColor}
        isLoading={isNil(props.count)}
      >
        <HStack justify="flex-end" minWidth={COMPACT_SKELETON_WIDTH}>
          <Display size={2}>{props.count?.toString() ?? ''}</Display>
        </HStack>
      </SkeletonLoadingBox>
    </HStack>
  );
}
